import { useCallback, useEffect, useState } from "react";

import {
  AMBIENT_SCRIBE_STATUS_CANCELLED,
  AMBIENT_SCRIBE_STATUS_FAILED,
  AMBIENT_SCRIBE_STATUS_PROCESSING
} from "features/medical-scribe/medical-scribe-constants";
import type { AmbientScribe } from "services/types/ambient-scribe.type";

import useOnDepsChange from "./use-on-deps-change.hook";

/**
 * Frees a local generation latch if the backend never reports progress (SN-19067). The
 * server-side lock self-heals in 15 minutes, but a `202` whose flag is never observed `true`
 * would otherwise spin for the rest of the session.
 */
export const GENERATION_LATCH_TIMEOUT_MS = 180_000;

export type GenerationKind =
  | "soap"
  | "summary"
  | "transcript"
  | "audiologySummary"
  | "afterVisitSummary"
  | "debrief";

export type GenerationLatches = Record<GenerationKind, boolean>;

const NO_LATCHES: GenerationLatches = {
  soap: false,
  summary: false,
  transcript: false,
  audiologySummary: false,
  afterVisitSummary: false,
  debrief: false
};

type LatchSignals = Pick<
  AmbientScribe,
  | "soap_notes"
  | "summary"
  | "has_transcription"
  | "is_generating_soap"
  | "is_generating_summary"
  | "is_generating_audiology_summary"
  | "is_generating_after_visit_summary"
  | "consult_debrief_id"
>;

/**
 * Which latches the server has now answered: content arrived, or it says the run is over.
 *
 * A terminal failure status (FAILED/CANCELLED) answers every latch: some async outcomes (e.g. an
 * empty or not-ready transcription) end on the server without ever flipping is_generating_* to
 * true or producing content, so nothing else would ever clear them. COMPLETED does NOT: scribes
 * are usually COMPLETED when SOAP generation begins, and clearing on it would drop the spinner
 * before the backend reports progress.
 */
function getAnsweredLatches(
  remote: LatchSignals | undefined,
  isTerminalFailure: boolean
): GenerationLatches {
  if (isTerminalFailure) {
    return {
      soap: true,
      summary: true,
      transcript: true,
      audiologySummary: true,
      afterVisitSummary: true,
      debrief: true
    };
  }
  return {
    soap: Boolean(remote?.soap_notes) || remote?.is_generating_soap === false,
    summary:
      Boolean(remote?.summary) || remote?.is_generating_summary === false,
    transcript: Boolean(remote?.has_transcription),
    // `=== false` deliberately: these fields are `null` until the first run ever, and a nullish
    // check would clear the latch on the next poll, before the backend has flipped them to
    // `true`. The note never lands on the scribe, so there is no content field to fall back on.
    audiologySummary: remote?.is_generating_audiology_summary === false,
    afterVisitSummary: remote?.is_generating_after_visit_summary === false,
    // `consult_debrief_id` appearing is the only success signal (D8, ENG-1706).
    debrief: Boolean(remote?.consult_debrief_id)
  };
}

/** Clears each held latch the server has answered. Returns `latches` itself when nothing changes. */
export function clearAnsweredLatches(
  latches: GenerationLatches,
  remote: LatchSignals | undefined,
  isTerminalFailure: boolean
): GenerationLatches {
  const answered = getAnsweredLatches(remote, isTerminalFailure);
  const kinds = Object.keys(latches) as GenerationKind[];
  if (!kinds.some((kind) => latches[kind] && answered[kind])) return latches;
  return Object.fromEntries(
    kinds.map((kind) => [kind, latches[kind] && !answered[kind]])
  ) as GenerationLatches;
}

/**
 * Clears a held latch after `GENERATION_LATCH_TIMEOUT_MS`, calling `onTimeout` when it does.
 * `setLatch` and `onTimeout` must be stable, or every render restarts the timer.
 */
function useLatchTimeout(
  kind: GenerationKind,
  isHeld: boolean,
  setLatch: (kind: GenerationKind, isHeld: boolean) => void,
  onTimeout?: () => void
) {
  useEffect(() => {
    if (!isHeld) return undefined;
    const timeout = setTimeout(() => {
      setLatch(kind, false);
      onTimeout?.();
    }, GENERATION_LATCH_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [kind, isHeld, setLatch, onTimeout]);
}

/**
 * The spinners for each generate action. A click sets a local latch at once, so the UI moves
 * before the next poll confirms is_generating_* on the server; the latch clears when the server
 * answers.
 *
 * Clearing is edge-triggered: it is checked only when one of the server fields changes, never on
 * the click itself. On the click those fields still describe the PREVIOUS run (an unchanged
 * `is_generating_summary: false`, an old note), and checking them then would drop the spinner the
 * instant it appeared.
 */
export default function useScribeGenerationLatches(
  remote: AmbientScribe | undefined
) {
  const [latches, setLatches] = useState<GenerationLatches>(NO_LATCHES);
  // A terminal "gave up waiting" state, not an in-flight one: DebriefTab renders it as a distinct
  // "try again" state (see D8).
  const [debriefTimedOut, setDebriefTimedOut] = useState(false);

  const isTerminalFailure =
    remote?.status === AMBIENT_SCRIBE_STATUS_FAILED ||
    remote?.status === AMBIENT_SCRIBE_STATUS_CANCELLED;

  useOnDepsChange(
    [
      remote?.soap_notes,
      remote?.summary,
      remote?.has_transcription,
      remote?.is_generating_soap,
      remote?.is_generating_summary,
      remote?.is_generating_audiology_summary,
      remote?.is_generating_after_visit_summary,
      remote?.consult_debrief_id,
      isTerminalFailure
    ],
    () => {
      setLatches((held) =>
        clearAnsweredLatches(held, remote, isTerminalFailure)
      );
      // A debrief that actually completed makes any earlier timeout stale.
      if (remote?.consult_debrief_id) setDebriefTimedOut(false);
    }
  );

  const setLatch = useCallback((kind: GenerationKind, isHeld: boolean) => {
    setLatches((held) =>
      held[kind] === isHeld ? held : { ...held, [kind]: isHeld }
    );
  }, []);

  const markDebriefTimedOut = useCallback(() => setDebriefTimedOut(true), []);
  useLatchTimeout("audiologySummary", latches.audiologySummary, setLatch);
  useLatchTimeout("afterVisitSummary", latches.afterVisitSummary, setLatch);
  // Debrief generation has no progress flag at all (D8), so the timeout is the ONLY way this
  // latch clears on its own, and it says so rather than silently reverting to the idle CTA.
  useLatchTimeout("debrief", latches.debrief, setLatch, markDebriefTimedOut);

  const startDebrief = useCallback(() => {
    setDebriefTimedOut(false);
    setLatch("debrief", true);
  }, [setLatch]);

  return {
    latches,
    setLatch,
    startDebrief,
    debriefTimedOut,
    // Single truth per tab: local intent OR server state. Audiology and the letter never key off
    // is_generating_soap — their branch of POST /{id}/async/ leaves it untouched. The debrief
    // has no server flag (D8), so it is local intent only.
    isGeneratingSoap: latches.soap || remote?.is_generating_soap === true,
    isGeneratingSummary:
      latches.summary || remote?.is_generating_summary === true,
    isGeneratingAudiologySummary:
      latches.audiologySummary ||
      remote?.is_generating_audiology_summary === true,
    isGeneratingAfterVisitSummary:
      latches.afterVisitSummary ||
      remote?.is_generating_after_visit_summary === true
  };
}

/** Whether the details should poll: polling starts with the latch, with no gap before the server confirms. */
export function shouldPollScribe(
  latches: GenerationLatches,
  remote: AmbientScribe | undefined,
  isPausedCompleting: boolean
) {
  const isLocallyGenerating =
    latches.soap ||
    latches.summary ||
    latches.audiologySummary ||
    latches.afterVisitSummary ||
    latches.debrief;
  const isServerGenerating =
    remote?.is_generating_soap === true ||
    remote?.is_generating_summary === true ||
    remote?.is_generating_audiology_summary === true ||
    remote?.is_generating_after_visit_summary === true;
  return (
    isLocallyGenerating ||
    isServerGenerating ||
    remote?.status === AMBIENT_SCRIBE_STATUS_PROCESSING ||
    isPausedCompleting
  );
}
